// watch/TranscriptPanel.tsx — the watch page's Transcript tab (1.27.60, concept "Chapters |
// Transcript"): every caption line with its time. Click a line to jump there; the line
// playing now is marked and kept in view inside the panel's own scroll box (never the
// page's); a search box narrows the list. Language picker when there's more than one track.
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import type { CaptionTrack } from '@/core/models/post';
import type { Cue } from '../../captions/vtt';
import { formatClock } from '../../player/chapters';

const Box = styled.div`
  display: grid;
  gap: 10px;
`;

const Tools = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  input,
  select {
    padding: 7px 10px;
    border-radius: ${({ theme }) => theme.radius.sm};
    border: 1px solid ${({ theme }) => theme.color.glassBorder};
    background: ${({ theme }) => theme.color.surface};
    color: ${({ theme }) => theme.color.textPrimary};
    font: 400 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  }
  input {
    flex: 1 1 200px;
    min-width: 0;
  }
`;

const Lines = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 340px;
  overflow-y: auto;
  display: grid;
  gap: 2px;
  scrollbar-width: thin;
`;

const Line = styled.button<{ $on: boolean }>`
  display: grid;
  grid-template-columns: 56px 1fr;
  gap: 10px;
  width: 100%;
  padding: 7px 10px;
  border: 0;
  border-radius: 8px;
  background: ${({ theme, $on }) => ($on ? theme.color.surfaceRaised : 'transparent')};
  color: ${({ theme }) => theme.color.textPrimary};
  text-align: left;
  cursor: pointer;
  font: 400 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  line-height: 1.45;
  white-space: pre-line;
  &:hover {
    background: ${({ theme }) => theme.color.hover};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: -2px;
  }
  time {
    color: ${({ theme }) => theme.color.accentCyan};
    font: 600 ${({ theme }) => theme.fontSize.xs} ${({ theme }) => theme.font.body};
    font-variant-numeric: tabular-nums;
    padding-top: 2px;
  }
`;

const Note = styled.p`
  margin: 0;
  font: 400 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  color: ${({ theme }) => theme.color.textMuted};
`;

export interface TranscriptPanelProps {
  tracks: CaptionTrack[];
  language: string;
  onLanguage: (language: string) => void;
  cues: Cue[] | null;
  /** Start of the line playing now. */
  activeStart: number | null;
  onSeek: (seconds: number) => void;
}

export function TranscriptPanel({
  tracks,
  language,
  onLanguage,
  cues,
  activeStart,
  onSeek,
}: TranscriptPanelProps) {
  const [query, setQuery] = useState('');
  const listRef = useRef<HTMLOListElement>(null);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (cues ?? []).filter((c) => !q || c.text.toLowerCase().includes(q));
  }, [cues, query]);
  const track = tracks.find((t) => t.language === language);

  // Keep the line playing now in view — scrolling the panel only, never the page.
  useEffect(() => {
    const list = listRef.current;
    if (!list || activeStart === null || query) return;
    const line = list.querySelector<HTMLElement>(`[data-start="${activeStart}"]`);
    if (!line) return;
    const top = line.offsetTop - list.offsetTop;
    if (top < list.scrollTop || top + line.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = Math.max(0, top - list.clientHeight / 3);
    }
  }, [activeStart, query]);

  return (
    <Box>
      <Tools>
        <input
          type="search"
          aria-label="Search the transcript"
          placeholder="Search the transcript"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {tracks.length > 1 && (
          <select
            aria-label="Transcript language"
            value={language}
            onChange={(e) => onLanguage(e.target.value)}
          >
            {tracks.map((t) => (
              <option key={t.language} value={t.language}>
                {t.label}
              </option>
            ))}
          </select>
        )}
      </Tools>
      {track?.source === 'auto' && (
        <Note>These captions were made automatically, so some words may be wrong.</Note>
      )}
      {cues === null ? (
        <Note role="status">Loading the transcript…</Note>
      ) : shown.length === 0 ? (
        <Note>{query ? 'Nothing in the transcript matches that.' : 'No transcript.'}</Note>
      ) : (
        <Lines ref={listRef} aria-label="Transcript">
          {shown.map((c) => (
            <li key={c.start}>
              <Line
                type="button"
                data-start={c.start}
                $on={c.start === activeStart}
                aria-current={c.start === activeStart || undefined}
                onClick={() => onSeek(c.start)}
              >
                <time>{formatClock(c.start)}</time>
                <span>{c.text}</span>
              </Line>
            </li>
          ))}
        </Lines>
      )}
    </Box>
  );
}
